
import { useState, useEffect, useRef } from "react"
import { Link, useNavigate, useLocation } from "react-router-dom"
import { useDispatch, useSelector } from "react-redux"
import swal from "sweetalert"
import Loader from "../Common/Loader"
import { getCampaignAction, deleteCampaignAction } from "../../../store/actions/CampaignAction"

const Campaign = () => {
    const navigate = useNavigate()
    const location = useLocation()
    const dispatch = useDispatch()
    const isFirstLoad = useRef(true);

    const campaigns = useSelector((state) => state.campaign.list || [])
    const numRows = useSelector((state) => state.campaign.num_rows || 0)
    let searchTimeout = null;

    const [searchQueries, setSearchQueries] = useState({
        title: "",
        campaign_status: "",
    })
    const [searchTitle, setSearchTitle] = useState("")
    const [searchStatus, setSearchStatus] = useState("")

    const [sortField, setSortField] = useState("title")
    const [sortOrder, setSortOrder] = useState("asc")
    const [currentPage, setCurrentPage] = useState(0)
    const rowsPerPage = 10
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState("")

    // Track active search for server-side filtering
    const [activeSearchField, setActiveSearchField] = useState("")
    const [activeSearchKeyword, setActiveSearchKeyword] = useState("")

    const filteredData = campaigns.filter((campaign) => {
        if (activeSearchField === "title" && campaign.title.toLowerCase().indexOf(activeSearchKeyword.toLowerCase()) === -1) {
            return false;
        }
        if (activeSearchField === "campaign_status" && campaign.campaign_status.toString().toLowerCase().indexOf(activeSearchKeyword.toLowerCase()) === -1) {
            return false;
        }
        return true;
    });

    const paginatedData = filteredData.slice(currentPage * rowsPerPage, (currentPage + 1) * rowsPerPage);

    const totalPages = Math.ceil(numRows / rowsPerPage)

    const handleEdit = (item) => {
        navigate("/add-campaign", { state: { item } })
    }

    const handleDelete = (id) => {
        swal({
            title: "Are you sure?",
            text: "This campaign will be deleted permanently.",
            icon: "warning",
            buttons: true,
            dangerMode: true
        }).then(async (yes) => {
            if (yes) {
                try {
                    setLoading(true)
                    await dispatch(deleteCampaignAction(id))
                    // Refresh the current page after deletion
                    await fetchCampaignsFromServer(
                        activeSearchField,
                        activeSearchKeyword,
                        true,
                        currentPage,
                        sortField,
                        sortOrder
                    )
                } catch (err) {
                    setError("Failed to delete campaign")
                    console.error("Delete failed:", err)
                } finally {
                    setLoading(false)
                }
            }
        })
    }

    const fetchCampaignsFromServer = async (
        search_by = "",
        search_keyword = "",
        pagination = true,
        pageid = 0,
        sort_by = sortField,
        sort_order = sortOrder
      ) => {
        try {
          // ✅ show loader only on first page visit
          if (isFirstLoad.current) {
            setLoading(true);
          }
      
          await dispatch(
            getCampaignAction(
              pagination,
              pageid,
              search_by,
              search_keyword,
              sort_by,
              sort_order
            )
          );
        } catch (err) {
          console.error("Failed to fetch campaigns:", err);
          setError("Failed to load campaign data.");
        } finally {
          setLoading(false);
          isFirstLoad.current = false; // ✅ stop loader forever
        }
      };
      
      

    const handlePageChange = (newPage) => {
        setCurrentPage(newPage);
    }

    useEffect(() => {
        // Check if returning from edit page with state
        if (location?.state?.returnTo) {
            const { currentPage: prevPage, searchQueries: prevSearches, sortField: prevSort, sortOrder: prevOrder } = location.state.returnTo
            setCurrentPage(prevPage || 0)
            setSearchQueries(prevSearches || {})
            setSortField(prevSort || "title")
            setSortOrder(prevOrder || "asc")

            // Determine the search field and keyword from restored state
            const searchField = prevSearches?.title ? "title" : prevSearches?.campaign_status ? "campaign_status" : ""
            const searchKeyword = prevSearches?.title || prevSearches?.campaign_status || ""

            setActiveSearchField(searchField)
            setActiveSearchKeyword(searchKeyword)

            // Fetch with restored state
            fetchCampaignsFromServer(
                searchField,
                searchKeyword,
                true,
                prevPage || 0,
                prevSort || "title",
                prevOrder || "asc"
            )
        } else {
            fetchCampaignsFromServer()
        }
    }, [])

    // ================= SEARCH =================
    const handleSearch = (e, field) => {
        const keyword = e.target.value;
      
        const clearedQueries = {
          title: "",
          campaign_status: "",
        };
      
        const updatedQueries = { ...clearedQueries, [field]: keyword };
      
        setSearchQueries(updatedQueries);
        setCurrentPage(0);
        setActiveSearchField(field);
        setActiveSearchKeyword(keyword);
      
        // ✅ debounce API call
        if (searchTimeout) clearTimeout(searchTimeout);
      
        searchTimeout = setTimeout(() => {
          fetchCampaignsFromServer(
            field,
            keyword,
            true,
            0,
            sortField,
            sortOrder
          );
        }, 400);
      };
      

    // ================= SORT =================
    const handleSort = (field) => {
        const order = sortField === field && sortOrder === "asc" ? "desc" : "asc"
        setSortField(field)
        setSortOrder(order)
        setCurrentPage(0)
        fetchCampaignsFromServer(activeSearchField, activeSearchKeyword, true, 0, field, order)
    }

    // ================= PAGINATION =================

    const toggleSort = (field) => {
        handleSort(field);
    }

    return (
        <div>
            <div className="row mb-3">
                <div className="col-12 text-end">
                    <Link
                        to="/add-campaign"
                        state={{
                            returnTo: {
                                currentPage,
                                searchQueries,
                                sortField,
                                sortOrder,
                            },
                        }}
                        className="btn btn-primary btn-rounded"
                    >
                        + Add Campaign
                    </Link>
                </div>
            </div>

            <div className="card">
                <div className="card-body">
                    {error && <div className="text-danger mb-2">{error}</div>}
                    {loading ? (
                        <div className="text-center my-5">
                            <Loader size="lg" />
                        </div>
                    ) : (
                        <>
                        <div className="table-responsive">
                            <table className="table table-striped mb-4">
                                <thead className="table-light">
                                    <tr>
                                        {/* <th>#</th> */}
                                        <th onClick={() => handleSort("title")} style={{ cursor: "pointer" }}>
                                            Title {sortField === "title" && (sortOrder === "asc" ? "↑" : "↓")}
                                            <input
                                                className="form-control form-control-sm mt-1"
                                                placeholder="Search title"
                                                value={searchQueries.title}
                                                onChange={(e) =>
                                                    handleSearch(e, "title")
                                                }
                                                onClick={(e) => e.stopPropagation()}
                                            />
                                        </th>

                                        <th onClick={() => handleSort("campaign_status")} style={{ cursor: "pointer" }}>
                                            Status {sortField === "campaign_status" && (sortOrder === "asc" ? "↑" : "↓")}
                                            <input
                                                className="form-control form-control-sm mt-1"
                                                placeholder="Search status"
                                                value={searchQueries.campaign_status}
                                                onChange={(e) => handleSearch(e, "campaign_status")}
                                                onClick={(e) => e.stopPropagation()}
                                            />
                                        </th>

                                        <th>Dates</th>
                                        <th>Consultation</th>
                                        <th>Discount (%)</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {campaigns?.length > 0 ? (
                                        campaigns.map((item, index) => (
                                            <tr key={item.id}>
                                                {/* <td>{currentPage * rowsPerPage + index + 1}</td> */}
                                                <td>{item.title}</td>
                                                <td>
                                                    <span
                                                      
                                                       className={`badge px-3 py-1 rounded-pill text-white ${
                                                         item.campaign_status === "Active"
                                                           ? "bg-success"
                                                           : "bg-danger"
                                                       }`}
                                                     
                                                     
                                                    >
                                                      {item.campaign_status === "Active" ? "Active" : "Inactive"}

                                                    </span>
                                                </td>

                                                <td>
                                                    {item.start_date} → {item.end_date}
                                                </td>

                                                <td>₹ {item.consultation_charges}</td>
                                                <td>{item.medicinal_discount}%</td>

                                                <td>
                                                    <div className="d-flex">
                                                        <button
                                                            className="btn btn-link p-0 me-3"
                                                            onClick={() =>
                                                                navigate("/add-campaign", {
                                                                    state: {
                                                                        item,
                                                                        returnTo: {
                                                                            currentPage,
                                                                            searchQueries,
                                                                            sortField,
                                                                            sortOrder,
                                                                        },
                                                                    },
                                                                })
                                                            }
                                                        >
                                                            <i className="fa fa-pencil-alt text-success fs-18"></i>
                                                        </button>

                                                        <button
                                                            className="btn btn-link p-0"
                                                            onClick={() =>
                                                                handleDelete(item.campaign_id)
                                                            }
                                                        >
                                                            <i className="fa fa-trash text-danger fs-18"></i>
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="7" className="text-center">
                                                No Campaigns found.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                            {/* Pagination */}
                            <div
                                className="pagination-wrapper"
                                style={{
                                    display: "flex",
                                    gap: "6px",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    marginTop: "20px",
                                    flexWrap: "wrap",
                                    flexDirection: "column",
                                }}
                            >
                                <div>
                                    Showing {campaigns?.length === 0 ? 0 : currentPage * rowsPerPage + 1} to{" "}
                                    {Math.min((currentPage + 1) * rowsPerPage, numRows)} of {numRows} entries
                                </div>

                                <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", justifyContent: "center" }}>
                                    <button
                                        onClick={() => handlePageChange(Math.max(currentPage - 1, 0))}
                                        disabled={currentPage === 0}
                                        style={{
                                            padding: "6px 12px",
                                            border: "1px solid #ddd",
                                            borderRadius: "4px",
                                            backgroundColor: "#fff",
                                            color: "#000",
                                            cursor: currentPage === 0 ? "not-allowed" : "pointer",
                                            opacity: currentPage === 0 ? 0.5 : 1,
                                        }}
                                    >
                                        &lt;
                                    </button>

                                    {(() => {
                                        const pages = [];
                                        const maxPagesToShow = 5;
                                        const startPage = Math.max(
                                            0,
                                            Math.min(currentPage - 2, totalPages - maxPagesToShow)
                                        );
                                        const endPage = Math.min(startPage + maxPagesToShow, totalPages);

                                        if (startPage > 0) {
                                            pages.push(
                                                <button
                                                    key={0}
                                                    onClick={() => handlePageChange(0)}
                                                    style={{
                                                        padding: "6px 12px",
                                                        border: "1px solid #ddd",
                                                        borderRadius: "4px",
                                                        backgroundColor: "#fff",
                                                        color: "#000",
                                                        cursor: "pointer",
                                                    }}
                                                >
                                                    1
                                                </button>
                                            );
                                            if (startPage > 1) {
                                                pages.push(
                                                    <span key="dots1" style={{ padding: "6px 12px", userSelect: "none" }}>
                                                        ...
                                                    </span>
                                                );
                                            }
                                        }

                                        for (let i = startPage; i < endPage; i++) {
                                            const isActive = currentPage === i;
                                            pages.push(
                                                <button
                                                    key={i}
                                                    onClick={() => handlePageChange(i)}
                                                    style={{
                                                        padding: "6px 12px",
                                                        borderRadius: "4px",
                                                        border: "1px solid #ddd",
                                                        backgroundColor: isActive ? "#007bff" : "#fff",
                                                        color: isActive ? "#fff" : "#000",
                                                        cursor: "pointer",
                                                    }}
                                                >
                                                    {i + 1}
                                                </button>
                                            );
                                        }

                                        if (endPage < totalPages) {
                                            if (endPage < totalPages - 1) {
                                                pages.push(
                                                    <span key="dots2" style={{ padding: "6px 12px", userSelect: "none" }}>
                                                        ...
                                                    </span>
                                                );
                                            }
                                            pages.push(
                                                <button
                                                    key={totalPages - 1}
                                                    onClick={() => handlePageChange(totalPages - 1)}
                                                    style={{
                                                        padding: "6px 12px",
                                                        border: "1px solid #ddd",
                                                        borderRadius: "4px",
                                                        backgroundColor: "#fff",
                                                        color: "#000",
                                                        cursor: "pointer",
                                                    }}
                                                >
                                                    {totalPages}
                                                </button>
                                            );
                                        }

                                        return pages;
                                    })()}

                                    <button
                                        onClick={() => handlePageChange(Math.min(currentPage + 1, totalPages - 1))}
                                        disabled={currentPage === totalPages - 1}
                                        style={{
                                            padding: "6px 12px",
                                            border: "1px solid #ddd",
                                            borderRadius: "4px",
                                            backgroundColor: "#fff",
                                            color: "#000",
                                            cursor: currentPage === totalPages - 1 ? "not-allowed" : "pointer",
                                            opacity: currentPage === totalPages - 1 ? 0.5 : 1,
                                        }}
                                    >
                                        &gt;
                                    </button>
                                </div>
                            </div>
                            </>
                    )}

                </div>
            </div>
        </div>
    )
}

export default Campaign
